import { useT } from "@repo/transifex";
import { useCurrentTillLocalisation } from "~/api/tills/hooks/use-till-current/localisation";
import { ReportTableSection } from "~/components/report";
import { useVatLabel } from "~/hooks/use-vat-label";
import { priceFormatter } from "~/utils/price-formatter";

import { type VatSummaryData } from "./types";

type VatSummarySectionProps = {
  data: VatSummaryData;
};

export const VatSummarySection = ({ data }: VatSummarySectionProps) => {
  const t = useT();
  const { locale } = useCurrentTillLocalisation();
  const { vatLabel } = useVatLabel();

  const fmt = (cents: number) =>
    priceFormatter({ value: cents, currency: data.currency, locale });

  // The backend only provides the rate percentage (no name), so show it as-is
  // rather than inventing/translating a "Normal"/"Exempt" label.
  const formatRateName = (percentage: number) => `${percentage}%`;

  const title = t("{vatLabel} Summary", { vatLabel });
  const rateColumn = {
    key: "rateName",
    label: t("{vatLabel} Rate", { vatLabel }),
    align: "left" as const
  };
  const amountColumn = {
    key: "vatAmount",
    label: t("{vatLabel} Amount", { vatLabel }),
    align: "right" as const
  };

  if (!data.hasFullData) {
    const columns = [rateColumn, amountColumn];

    const rows = data.rows.map((row) => ({
      rateName: formatRateName(row.percentage),
      vatAmount: fmt(row.vatAmount)
    }));

    const footer = {
      rateName: t("TOTAL"),
      vatAmount: fmt(data.totals.vatAmount)
    };

    return (
      <ReportTableSection
        title={title}
        columns={columns}
        rows={rows}
        footer={footer}
      />
    );
  }

  const columns = [
    rateColumn,
    {
      key: "salesExclVat",
      label: t("Sales Excl. {vatLabel}", { vatLabel }),
      align: "right" as const
    },
    amountColumn,
    {
      key: "salesInclVat",
      label: t("Sales Incl. {vatLabel}", { vatLabel }),
      align: "right" as const
    }
  ];

  const rows = data.rows.map((row) => ({
    rateName: formatRateName(row.percentage),
    salesExclVat: fmt(row.salesExclVat),
    vatAmount: fmt(row.vatAmount),
    salesInclVat: fmt(row.salesInclVat)
  }));

  const footer = {
    rateName: t("TOTAL"),
    salesExclVat: fmt(data.totals.salesExclVat),
    vatAmount: fmt(data.totals.vatAmount),
    salesInclVat: fmt(data.totals.salesInclVat)
  };

  return (
    <ReportTableSection
      title={title}
      columns={columns}
      rows={rows}
      footer={footer}
    />
  );
};
